/**
 * Titan — reposition the Stoq collection button into the quick-add slot.
 *
 * Stoq (Restock Rocket) injects a `.restock-rocket-button-collection` button
 * into each <product-card> at runtime, and base.css pins it as an absolute,
 * hover-reveal overlay at the bottom of the whole card. We instead want it in
 * the exact slot the Add to cart / Quick view button occupies: overlaid at the
 * bottom of the card gallery (`.card-gallery`), i.e. above the product title.
 *
 * This script only MOVES the button within its own <product-card> (so it stays
 * a descendant of any [data-discord-link] wrapper and Stoq's own click handling
 * / context tracking keep working). It adds a marker class so the scoped CSS in
 * titan-stoq-collection-position.css can re-anchor it to the gallery without
 * affecting the button anywhere else.
 *
 * The button's BEHAVIOUR is left alone: base.css still owns the hover reveal
 * (it keys off `product-card:is(:hover, :focus-within)`, which the button is
 * still inside) and the button's look. Only its box moves.
 *
 * `.card-gallery` is `position: relative`, so it is already the containing block
 * for absolutely positioned descendants (that is how `.quick-add` positions
 * itself). Appending the button there is enough for the CSS to line it up with
 * the quick-add button. A card without a gallery is left untouched, so it keeps
 * base.css's original placement.
 *
 * Locked cards (`.titan-locked-info` present) are skipped entirely — their
 * button is handled separately by titan-locked-stoq-move.js. Cards for products
 * tagged `upcoming-product` (`.titan-coming-info` present) are skipped for the
 * same reason: titan-coming-product-card.js owns those buttons.
 *
 * Stoq injects asynchronously at an unknown time, so we run on DOM ready, watch
 * for late injection with a MutationObserver, and re-run on theme-editor section
 * loads. Relocation is idempotent.
 *
 * One behaviour IS added, because the reveal alone cannot survive it: opening
 * Stoq's "Notify me" popup moves both the pointer and the focus out of the card,
 * so `product-card:is(:hover, :focus-within)` stops matching and base.css slides
 * the button back down behind the popup. Clicking the button therefore pins it
 * with `.titan-stoq-button-open`, which holds the revealed opacity/transform
 * until the popup is closed. See pinOpenButton() below.
 */
(function () {
  'use strict';

  var BUTTON_SELECTOR = '.restock-rocket-button-collection';
  var POSITIONED_CLASS = 'titan-stoq-collection-positioned';
  var OPEN_CLASS = 'titan-stoq-button-open';

  /* Stoq's popup is appended outside the cards and its markup is the app's, not
     ours, so we match it by shape rather than by one known selector: anything
     modal/popup/overlay/dialog-ish carrying a restock-rocket or stoq token, in
     both the dash and underscore spellings the app has shipped. Deliberately
     broad — a miss only means we fall back to the pointer/Escape unpin paths
     below, never a wrong element. Button classes are excluded by requiring the
     second token. */
  var MODAL_SELECTOR = [
    '[class*="restock-rocket-modal"]',
    '[class*="restock_rocket_modal"]',
    '[id*="restock-rocket-modal"]',
    '[id*="restock_rocket_modal"]',
    '[class*="restock-rocket-popup"]',
    '[class*="restock-rocket-widget"]',
    '[class*="restock-rocket"][class*="overlay"]',
    '[class*="stoq-modal"]',
    '[id*="stoq-modal"]',
    '[class*="stoq"][class*="popup"]',
    '[class*="stoq"][class*="overlay"]',
    '[role="dialog"][class*="restock"]',
    '[role="dialog"][class*="stoq"]'
  ].join(',');

  // How long to wait for the popup to show up before giving up on detecting it.
  var MODAL_GRACE_MS = 2500;
  var MODAL_POLL_MS = 200;

  function repositionButtons() {
    var buttons = document.querySelectorAll(BUTTON_SELECTOR);
    if (!buttons.length) return;

    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];

      var card = btn.closest('product-card');
      if (!card) continue;

      // Locked cards manage this button separately — never touch them.
      if (card.querySelector('.titan-locked-info') || btn.closest('.titan-locked-stoq-wrapper')) {
        continue;
      }

      // Same for `upcoming-product` cards, which titan-coming-product-card.js
      // moves into the info area below the title.
      if (card.querySelector('.titan-coming-info') || btn.closest('.titan-coming-stoq-wrapper')) {
        continue;
      }

      // New home: last child of the gallery, alongside <quick-add-component>.
      // No gallery means an unusual card layout — leave that button exactly
      // where base.css puts it rather than guessing.
      var gallery = card.querySelector('.card-gallery');
      if (!gallery) continue;

      // Already in the right place — nothing to do (keeps this idempotent).
      if (btn.parentElement !== gallery || gallery.lastElementChild !== btn) {
        gallery.appendChild(btn);
      }

      if (!btn.classList.contains(POSITIONED_CLASS)) {
        btn.classList.add(POSITIONED_CLASS);
      }
    }
  }

  /* ---------------------------------------------------------------------------
     Keeping the button put while Stoq's popup is open.
     --------------------------------------------------------------------------- */

  var openButton = null; // the currently pinned button, if any
  var openCard = null; // its <product-card>
  var pollTimer = null;
  var pollStarted = 0;
  var modalSeen = false; // the popup was positively identified at least once
  var pointerLeftCard = false; // the pointer has been away since we pinned

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    if (!el.getClientRects().length) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    return parseFloat(cs.opacity) > 0.01;
  }

  function modalIsOpen() {
    var nodes = document.querySelectorAll(MODAL_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      // The popup lives outside the grid; anything inside a card is not it.
      if (nodes[i].closest('product-card')) continue;
      if (isVisible(nodes[i])) return true;
    }
    return false;
  }

  function onCardPointerLeave() {
    pointerLeftCard = true;
  }

  function onCardPointerEnter() {
    // The pointer is back on the card, so :hover is holding the button up — we
    // can drop the pin with no visual shift at all.
    if (pointerLeftCard) unpinOpenButton();
  }

  function onDocumentKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') unpinOpenButton();
  }

  function unpinOpenButton() {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (openCard) {
      openCard.removeEventListener('pointerleave', onCardPointerLeave);
      openCard.removeEventListener('pointerenter', onCardPointerEnter);
    }
    document.removeEventListener('keydown', onDocumentKeydown, true);
    if (openButton) openButton.classList.remove(OPEN_CLASS);
    openButton = null;
    openCard = null;
    modalSeen = false;
    pointerLeftCard = false;
  }

  function pollModal() {
    // The app sometimes swaps the button node while the popup is still up (e.g.
    // it re-renders as "You're subscribed"). Carry the pin over to the new node
    // rather than dropping it — repositionButtons() adds POSITIONED_CLASS when
    // it moves that node into the gallery, and the CSS needs both classes, so
    // an early re-pin simply has no effect until then.
    if (!openButton || !openButton.isConnected) {
      var replacement = openCard ? openCard.querySelector(BUTTON_SELECTOR) : null;
      if (!replacement) {
        unpinOpenButton();
        return;
      }
      openButton = replacement;
      openButton.classList.add(OPEN_CLASS);
    }

    if (modalIsOpen()) {
      modalSeen = true;
      return;
    }

    // Seen and now gone — the popup was closed, so release the button.
    if (modalSeen) {
      unpinOpenButton();
      return;
    }

    // Never found it: our selectors do not match this build of the app. Stop
    // polling but stay pinned, and let the pointer-re-entry / Escape handlers
    // release the button. Staying visible is harmless; shifting is not.
    if (Date.now() - pollStarted > MODAL_GRACE_MS) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function pinOpenButton(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var btn = target.closest(BUTTON_SELECTOR + '.' + POSITIONED_CLASS);
    if (!btn) return;

    // Clicking a different card's button releases the previous one.
    if (openButton && openButton !== btn) unpinOpenButton();
    if (openButton === btn) return;

    openButton = btn;
    openCard = btn.closest('product-card');
    modalSeen = false;
    pointerLeftCard = false;
    btn.classList.add(OPEN_CLASS);

    if (openCard) {
      openCard.addEventListener('pointerleave', onCardPointerLeave);
      openCard.addEventListener('pointerenter', onCardPointerEnter);
    }
    document.addEventListener('keydown', onDocumentKeydown, true);

    pollStarted = Date.now();
    if (pollTimer !== null) clearInterval(pollTimer);
    pollTimer = setInterval(pollModal, MODAL_POLL_MS);
  }

  function init() {
    repositionButtons();

    // Catch Stoq's asynchronous injection (and any later re-render that moves
    // the button back out). repositionButtons() is a cheap no-op once the
    // button is already in place.
    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function (mutations) {
        var shouldRun = false;
        for (var i = 0; i < mutations.length && !shouldRun; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (
              node.classList.contains('restock-rocket-button-collection') ||
              node.querySelector('.restock-rocket-button-collection')
            ) {
              shouldRun = true;
              break;
            }
          }
        }
        if (shouldRun) repositionButtons();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // Pin the button on activation, before Stoq's popup can steal hover/focus.
    // Capture phase so it still fires if the app stops propagation; pointerdown
    // and click are both bound (keyboard activation only emits the latter) and
    // pinning is idempotent.
    document.addEventListener('pointerdown', pinOpenButton, true);
    document.addEventListener('click', pinOpenButton, true);

    // Fallback ticks in case the observer is unavailable or misses early frames.
    setTimeout(repositionButtons, 800);
    setTimeout(repositionButtons, 1800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run when a section is re-rendered.
  document.addEventListener('shopify:section:load', repositionButtons);
})();
